import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import { EyeIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import DOMPurify from 'isomorphic-dompurify';
import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { emojify } from '../emoji';

interface MarkdownProps {
  text: string;
  onChange?: (value: string) => void;
  label?: string;
  minHeight?: number;
  hideBorder?: boolean;
}

const Markdown: React.FC<MarkdownProps> = ({ text, onChange, label, minHeight = 100, hideBorder = false }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableText, setEditableText] = useState(text);

  useEffect(() => {
    setEditableText(text);
  }, [text]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditableText(e.target.value);
  };

  const handleSave = () => {
    if (onChange) {
      onChange(editableText);
      setIsEditMode(false);
    }
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      handleSave();
    } else {
      setIsEditMode(true);
    }
  };

  // If onChange is not provided, we only support view mode
  const canEdit = !!onChange;
  const sanitized = useMemo(() => DOMPurify.sanitize(emojify(text), { USE_PROFILES: { "html": true } }), [text]);

  return (
    <Stack sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="subtitle1"
          sx={{ mb: 1, fontWeight: 'medium' }}
        >
          {label}
        </Typography>
      )}

      {isEditMode && canEdit ? (
        <Box sx={{ position: 'relative' }}>
          <TextField
            multiline
            fullWidth
            minRows={4}
            value={editableText}
            onChange={handleTextChange}
            onBlur={handleSave}
            variant="outlined"
            placeholder="Write markdown here..."
            sx={{ mb: 1 }}
          />
          <IconButton
            onClick={toggleEditMode}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
            }}
            size="small"
          >
            <EyeIcon size={20} />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', borderRadius: 2, border: hideBorder ? 'none' : '1px solid', borderColor: 'divider' }}>
          <Box
            sx={{
              color: 'text.primary',
              marginRight: canEdit ? '40px' : 0,
              minHeight: minHeight,
              padding: hideBorder ? 0 : 2,
              '& p': { margin: 0 },
              '& a': { color: 'primary.main' },
              '& img': { maxWidth: '100%' },
              '& pre': {
                p: 1.5,
                overflowX: 'auto'
              },
              '& code': {
                px: 1,
                py: 0.5,
                borderRadius: 1
              },
              '& ul': {
                margin: 0.5,
              },
              '& ol': {
                margin: 0.5,
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                overflowX: 'auto',
                border: '1px solid',
                borderColor: 'text.secondary',
              },
              '& th, & td': {
                padding: 1,
                border: '1px solid',
                borderColor: 'text.secondary',
              },
            }}
          >
            {/* TODO Custom rehype plugin to replace :flag_xx: with flag icons */}
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={canEdit ? [] : [rehypeRaw]}>{canEdit ? editableText : sanitized}</ReactMarkdown>
          </Box>
          {canEdit && (
            <IconButton
              onClick={toggleEditMode}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
              }}
              size="small"
            >
              <PencilSimpleIcon size={20} />
            </IconButton>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default Markdown; 